import { Image as ImageIcon, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { PartnerFigure } from '../../contracts';
import { useEvidenceAnswerPorts } from './ports';

export function PartnerFigurePreview({ figure }: { figure: PartnerFigure }) {
  const { resolvePartnerFigureUrl } = useEvidenceAnswerPorts();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    setUrl(null);
    setFailed(false);

    void (async () => {
      try {
        const resolvedUrl = await resolvePartnerFigureUrl(figure.id);
        const response = await fetch(resolvedUrl);
        if (!response.ok) {
          throw new Error('figure fetch failed');
        }
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!active) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
          return;
        }
        setUrl(objectUrl);
      } catch {
        if (active) setFailed(true);
      }
    })();

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attempt, figure.id, resolvePartnerFigureUrl]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setLightboxOpen(false);
    };
    // Capture + preventDefault claims Esc, so page-wide Esc bindings (e.g. stop generating) stand down.
    document.addEventListener('keydown', closeOnEscape, { capture: true });
    return () =>
      document.removeEventListener('keydown', closeOnEscape, { capture: true });
  }, [lightboxOpen]);

  if (failed) return null;

  return (
    <figure className="overflow-hidden rounded-lg border border-border bg-background-tertiary">
      {!url ? (
        <div
          className="flex h-32 items-center justify-center gap-2 text-text-tertiary"
          data-testid="partner-figure-loading"
        >
          <Loader2 size={16} className="animate-spin" />
          <ImageIcon size={16} />
        </div>
      ) : (
        <button
          type="button"
          className="block w-full cursor-zoom-in bg-white" // theme-agnostic: clinical JPEGs keep a white plate in both themes
          onClick={(event) => {
            event.stopPropagation();
            setLightboxOpen(true);
          }}
          aria-label={figure.label || figure.caption || 'Open figure'}
        >
          {/* eslint-disable-next-line @next/next/no-img-element -- blob URL, never the signed query */}
          <img
            src={url}
            alt={figure.caption || figure.label || ''}
            className="max-h-64 w-full object-contain"
            data-dd-privacy="hidden"
            onError={() => {
              if (attempt === 0) {
                setAttempt(1);
              } else {
                setFailed(true);
              }
            }}
          />
        </button>
      )}
      {(figure.label || figure.caption) && (
        <figcaption className="px-3 py-2 text-xs leading-relaxed text-text-secondary">
          {figure.label && <span className="font-medium">{figure.label}</span>}
          {figure.label && figure.caption ? ' — ' : null}
          {figure.caption}
        </figcaption>
      )}

      {lightboxOpen &&
        url &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80">
            <button
              type="button"
              className="absolute inset-0 size-full cursor-default border-0 bg-transparent"
              onClick={() => setLightboxOpen(false)}
              aria-label="Close figure preview"
            />
            <button
              type="button"
              className="absolute end-4 top-4 z-10 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
              onClick={() => setLightboxOpen(false)}
              aria-label="Close"
            >
              <X className="size-5" />
            </button>
            <figure className="pointer-events-none relative z-10 flex max-h-[90vh] max-w-[90vw] flex-col items-center">
              {/* eslint-disable-next-line @next/next/no-img-element -- blob URL, never the signed query */}
              <img
                src={url}
                alt={figure.caption || figure.label || ''}
                className="pointer-events-auto max-h-[82vh] max-w-[90vw] rounded-lg bg-white object-contain" // theme-agnostic: clinical JPEGs keep a white plate in both themes
                data-dd-privacy="hidden"
              />
              {(figure.label || figure.caption) && (
                <figcaption className="mt-3 max-w-xl text-center text-sm text-white/80">
                  {figure.label && <span className="font-medium">{figure.label}</span>}
                  {figure.label && figure.caption ? ' — ' : null}
                  {figure.caption}
                </figcaption>
              )}
            </figure>
          </div>,
          document.body
        )}
    </figure>
  );
}
